import { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, EyeOff } from 'lucide-react';

const Hero3DGlobe = lazy(() => import('@/components/Hero3DGlobe'));

const Hero = ({ t, lang, userGeo }) => {
  const scrollTo = (href) => (e) => {
    e.preventDefault();
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="top" className="bg-grid relative pt-16 pb-24 sm:pt-24 sm:pb-28 lg:pt-28 lg:pb-32" data-testid="hero">
      <div className="orb float-slow" style={{ width: 360, height: 360, background: 'rgba(5,126,243,.35)', top: '-80px', left: '-80px' }} />
      <div className="orb float-slow" style={{ width: 460, height: 460, background: 'rgba(0,138,216,.28)', bottom: '-140px', right: '-120px', animationDelay: '2s' }} />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            className="chip mb-6"
            data-testid="hero-kicker"
          >
            <span className="dot" />
            {t.hero.kicker}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-display neon-title text-5xl sm:text-6xl lg:text-7xl font-[700] leading-[0.95] tracking-[-0.03em]"
            data-testid="hero-title"
          >
            <span className="block">{t.hero.title_a}</span>
            <span className="block text-[rgb(var(--brand-electric))]">{t.hero.title_b}</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-[rgba(5,126,243,.35)] bg-[rgba(5,126,243,.08)] px-3.5 py-1.5"
            data-testid="hero-signature"
          >
            <EyeOff size={14} className="text-[rgb(var(--brand-electric))]" />
            <span className="font-mono-accent text-[11px] uppercase tracking-[0.12em] text-white">{t.hero.signature}</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.16 }}
            className="mt-6 max-w-2xl text-base sm:text-lg text-white/75 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.22 }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="hero-ctas"
          >
            <Link to="/agenda" className="btn-primary" data-testid="hero-primary-cta">
              {t.meta.cta_primary} <ArrowRight size={16} />
            </Link>
            <a href="#services" onClick={scrollTo('#services')} className="btn-ghost" data-testid="hero-secondary-cta">
              <Play size={14} /> {t.meta.cta_secondary}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-2"
            data-testid="hero-trust-bar"
          >
            {t.hero.chips.map((tag) => (
              <span key={tag} className="chip"><span className="dot" />{tag}</span>
            ))}
          </motion.div>

          <p className="mt-6 text-xs kicker-mono text-white/50" data-testid="hero-trust">{t.hero.trust}</p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
            data-testid="hero-visual"
          >
            <Suspense fallback={
              <div className="hero3d-wrap grid place-items-center">
                <div className="kicker-mono text-white/50 animate-pulse">// initializing globe...</div>
              </div>
            }>
              <Hero3DGlobe userGeo={userGeo} lang={lang} />
            </Suspense>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
